import {
  createPopper,
  type MenuPopper,
  QuickToolMixin,
  QuickToolExtension,
} from '@blocksuite/affine-widget-edgeless-toolbar';
import { PageIcon } from '@blocksuite/icons/lit';
import { effect } from '@preact/signals-core';
import { css, html, LitElement } from 'lit';
import { state } from 'lit/decorators.js';

import { NoteTool, type NoteToolOption } from '../note-tool.js';
import type { EdgelessNoteMenu } from './note-menu.js';

export class EdgelessNoteToolButton extends QuickToolMixin(LitElement) {
  static override styles = css`
    :host {
      display: flex;
    }
  `;

  private _noteMenu: MenuPopper<EdgelessNoteMenu> | null = null;

  private readonly _states = ['childFlavour', 'childType', 'tip'] as const;

  override type = NoteTool;

  private _disposeMenu() {
    this._noteMenu?.dispose();
    this._noteMenu = null;
  }

  private _toggleNoteMenu() {
    if (this._noteMenu) {
      this._disposeMenu();
      this.requestUpdate();
    } else {
      this.gfx.tool.setTool(NoteTool, {
        childFlavour: this.childFlavour,
        childType: this.childType,
        tip: this.tip,
      });
      this._noteMenu = createPopper('edgeless-note-menu', this);

      this._noteMenu.element.edgeless = this.edgeless;
      this._noteMenu.element.childFlavour = this.childFlavour;
      this._noteMenu.element.childType = this.childType;
      this._noteMenu.element.tip = this.tip;
      this._noteMenu.element.onChange = (
        props: Partial<{
          childFlavour: NoteToolOption['childFlavour'];
          childType: string | null;
          tip: string;
        }>
      ) => {
        this._states.forEach(key => {
          // oxlint-disable-next-line eqeqeq
          if (props[key] != undefined) {
            Object.assign(this, { [key]: props[key] });
          }
        });
        this.gfx.tool.setTool(NoteTool, {
          childFlavour: this.childFlavour,
          childType: this.childType,
          tip: this.tip,
        });
      };
    }
  }

  override connectedCallback() {
    super.connectedCallback();
    this._disposables.add(
      effect(() => {
        const value = this.gfx.tool.currentToolName$.value;
        if (value !== 'affine:note') {
          this._disposeMenu();
        }
      })
    );
  }

  override disconnectedCallback() {
    this._disposeMenu();
    super.disconnectedCallback();
  }

  override render() {
    const { active } = this;
    return html`
      <edgeless-tool-icon-button
        class="edgeless-note-button"
        .tooltip=${this._noteMenu
        ? ''
        : html`<affine-tooltip-content-with-shortcut
              data-tip="${'Note'}"
              data-shortcut="${'N'}"
            ></affine-tooltip-content-with-shortcut>`}
        .tooltipOffset=${17}
        .active=${active}
        .iconContainerPadding=${6}
        .iconSize=${'32px'}
        @click=${() => {
        this._toggleNoteMenu();
      }}
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style="transform: scale(1.1); transform-origin: center;">
          <!-- Back Note: Cyan/Blue (rotated 8deg, offset top-right) -->
          <g transform="rotate(8 13 8)">
            <path d="M7 2H15.5L18 4.5V13C18 13.55 17.55 14 17 14H7C6.45 14 6 13.55 6 13V3C6 2.45 6.45 2 7 2Z" fill="#00A3E0"/>
            <path d="M15.5 2V4.5H18L15.5 2Z" fill="#0080B0"/>
            <path d="M8.5 6.5H15.5M8.5 9.5H15.5M8.5 11.5H12.5" stroke="#003B5C" stroke-width="1" stroke-linecap="round"/>
          </g>

          <!-- Front Note: Yellow (straight, offset bottom-left) -->
          <g>
            <path d="M4 9H12.5L15 11.5V20C15 20.55 14.55 21 14 21H4C3.45 21 3 20.55 3 20V10C3 9.45 3.45 9 4 9Z" fill="#FFC72C"/>
            <path d="M12.5 9V11.5H15L12.5 9Z" fill="#D9A300"/>
            <path d="M5.5 13.5H12.5M5.5 16.5H12.5M5.5 18.5H9.5" stroke="#6C5000" stroke-width="1.2" stroke-linecap="round"/>
          </g>
        </svg>
      </edgeless-tool-icon-button>
    `;
  }

  @state()
  accessor childFlavour: NoteToolOption['childFlavour'] = 'affine:paragraph';

  @state()
  accessor childType = 'text';

  @state()
  accessor tip = 'Text';
}

export const noteQuickTool = QuickToolExtension('note', ({ block }) => {
  return {
    type: 'note',
    content: html`<edgeless-note-tool-button
      .edgeless=${block}
    ></edgeless-note-tool-button>`,
    priority: 95,
  };
});

